/**
 * The message behind a failed request that asked for a blob response.
 *
 * With `responseType: 'blob'` axios hands the error body back as a Blob, so
 * the server's `{ "error": "..." }` is unreadable until it is decoded — and
 * the axios message alone is "Request failed with status code 500", which
 * is what the folder Excel download showed for a Carbon 400 (#1356).
 */
export async function blobErrorMessage(err: unknown): Promise<string> {
  const fallback = err instanceof Error ? err.message : String(err)
  const data = (err as { response?: { data?: unknown } })?.response?.data
  if (!(data instanceof Blob)) return fallback
  try {
    const text = await readText(data)
    try {
      const parsed = JSON.parse(text) as { error?: unknown }
      if (typeof parsed.error === 'string' && parsed.error) return parsed.error
    } catch {
      // Not JSON: the text itself is the message.
    }
    return text.trim() || fallback
  } catch {
    return fallback
  }
}

/** Blob.text() where it exists; jsdom's Blob has no text(), so fall back to FileReader. */
function readText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  })
}
